import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import { TextFieldVariants } from '@mui/material/TextField';
import classNames from 'classnames';
import React, { ReactElement, useCallback, useContext, useEffect, useState, memo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { NumericFormat, PatternFormat } from 'react-number-format';
import { ThemeContext, ThemeProvider } from 'styled-components';

import evaluteVisibilityFromDependencies from 'utils/evaluteVisibilityFromDependencies';
import generateValidationRules from 'utils/generateValidationRules';

import {
    AdornmentWrapper,
    ErrorIcon,
    SuccessIcon,
    ErrorText,
    FieldWrapper,
    HelperButton,
    HelperIcon,
    HelperTooltip,
    HelperTooltipText,
    StyledTextField,
} from './TextField.styled';
import TextFieldProps from './TextFieldProps';

const TextField = ({
    id,
    identifier,
    label,
    placeHolder,
    focused,
    disabled,
    type = 'text',
    predefinedValue,
    autoComplete,
    displayOption = 'Full',
    inputFormatNumber = false,
    inputFormatPattern,
    description,
    elementName,
    className,
    component,
    validators,
    control,
    endAdornment,
    dependencies,
    inputMode = 'text',
    pattern,
    size = 'medium',
    success,
    onChange,
    setValue,
    onFocus,
    tabIndex,
}: TextFieldProps): ReactElement => {
    const formContext = useFormContext();
    const themeContext = useContext(ThemeContext);
    const unregister = formContext?.unregister || undefined;
    const [hasBlurred, setHasBlurred] = useState(false);

    const isNumeric = component === 'NumberElementBlock' || component === 'CurrencyAmountElementBlock' || type === 'number';

    const classes = classNames(
        validators?.map((validator) => {
            return validator?.model?.validationCssClass || '';
        }),
    );

    const rules = validators && validators?.length > 0 ? generateValidationRules(validators) : undefined;

    const visibleFromDependencies = dependencies && control ? evaluteVisibilityFromDependencies(dependencies, control) : true;

    const isPositiveInteger = validators?.some((v) => v.type === 'PositiveIntegerValidator');

    const isRequired = !!(typeof rules?.required === 'boolean' ? rules.required : rules?.required?.value);

    useEffect(() => {
        if (elementName && setValue && unregister)
            if (visibleFromDependencies) setValue(elementName, predefinedValue);
            else unregister(elementName);
    }, [elementName, predefinedValue, setValue, unregister, visibleFromDependencies]);

    // Set the correct types and input modes for the input
    if (isNumeric) {
        inputMode = 'numeric';
        type = 'text';
        pattern = inputFormatNumber ? '^[0-9 ]*$' : '^[0-9]*$';
    }

    if (validators?.some((validator) => validator.type === 'EmailValidator')) {
        type = 'email';
        inputMode = 'email';
    }

    const getAdornment = useCallback(
        (error, showSuccess) => {
            if (error || endAdornment || showSuccess || description) {
                return (
                    <AdornmentWrapper>
                        {error && <ErrorIcon icon="error28" />}
                        {showSuccess && <SuccessIcon icon="check28" />}
                        {endAdornment}
                        {description && (
                            <HelperTooltip>
                                <HelperButton tabIndex={-1}>
                                    <HelperIcon icon="question28" />
                                </HelperButton>
                                <HelperTooltipText id={`${id || identifier}-helper-text`}>
                                    <span>{description}</span>
                                </HelperTooltipText>
                            </HelperTooltip>
                        )}
                    </AdornmentWrapper>
                );
            } else {
                return null;
            }
        },
        [endAdornment, description, id, identifier],
    );

    return (
        <MuiThemeProvider theme={createTheme()}>
            <ThemeProvider
                theme={{
                    ...themeContext,
                    displayOption,
                    size,
                }}
            >
                {visibleFromDependencies && (
                    <Controller
                        name={elementName || ''}
                        control={control}
                        defaultValue={predefinedValue || ''}
                        rules={rules}
                        render={({ field: { ref, ...field }, fieldState: { error, invalid, isDirty } }) => {
                            const hasValue = field.value !== null && field.value !== undefined && `${field.value}`.length > 0;

                            const showSuccess = success === undefined ? !error && !invalid && isDirty && hasBlurred && hasValue : success;
                            const adornment = getAdornment(error, showSuccess);

                            const elementProps = {
                                className: classes,
                                id: id || identifier,
                                inputRef: ref,
                                label: label,
                                placeholder: placeHolder,
                                focused: focused,
                                error: !!error,
                                disabled: disabled,
                                autoComplete: autoComplete,
                                inputProps: {
                                    inputMode: inputMode,
                                    pattern: pattern,
                                    tabIndex: tabIndex,
                                    'aria-describedby': description ? `${id || identifier}-helper-text` : undefined,
                                },
                                variant: 'outlined' as TextFieldVariants,
                                type: type,
                                InputProps: {
                                    endAdornment: adornment,
                                },
                                InputLabelProps: {
                                    shrink: field.value ? true : undefined,
                                    className: endAdornment ? 'Mui-adornment' : '',
                                },
                                onFocus: (event: React.FocusEvent<HTMLInputElement>) => {
                                    if (onFocus) {
                                        onFocus(event);
                                    }
                                },
                                onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                                    field.onChange(event);

                                    if (onChange) {
                                        onChange(event, event.target.value);
                                    }

                                    if (isNumeric && setValue && elementName) setValue(elementName, event.target.value.replace(/\D/g, ''));
                                },
                                onBlur: () => {
                                    field.onBlur();
                                    setHasBlurred(true);
                                },
                            };

                            return (
                                <FieldWrapper className={className}>
                                    {error && (
                                        <ErrorText>
                                            <span>{error.message}</span>
                                        </ErrorText>
                                    )}
                                    {!inputFormatNumber && !inputFormatPattern && (
                                        <StyledTextField
                                            aria-describedby="helper-text"
                                            {...field}
                                            {...elementProps}
                                            style={{
                                                width: '100%',
                                            }}
                                            $isValidated={showSuccess}
                                            required={isRequired}
                                        />
                                    )}
                                    {inputFormatNumber && !inputFormatPattern && (
                                        <NumericFormat
                                            {...field}
                                            {...elementProps}
                                            $isValidated={showSuccess}
                                            required={isRequired}
                                            customInput={StyledTextField}
                                            type="text"
                                            thousandSeparator=" "
                                            allowNegative={!isPositiveInteger}
                                            decimalScale={isPositiveInteger ? 0 : undefined}
                                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                if (onChange) {
                                                    onChange(event, event.target.value);
                                                }
                                            }}
                                            onValueChange={(formatValues) => {
                                                field.onChange(formatValues.value ? Number(formatValues.value) : '');
                                            }}
                                        />
                                    )}
                                    {inputFormatPattern && (
                                        <PatternFormat
                                            {...elementProps}
                                            {...field}
                                            $isValidated={showSuccess}
                                            required={isRequired}
                                            customInput={StyledTextField}
                                            format={inputFormatPattern}
                                            type="text"
                                            onChange={(event) => {
                                                if (onChange) {
                                                    onChange(event, event.target.value);
                                                }
                                            }}
                                            onValueChange={(formatValues) => {
                                                field.onChange(formatValues.formattedValue);
                                            }}
                                        />
                                    )}
                                </FieldWrapper>
                            );
                        }}
                    />
                )}
            </ThemeProvider>
        </MuiThemeProvider>
    );
};

export default memo(TextField);
